<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Innenraumfotografie für 360°-Virtual-Tours hat andere Anforderungen
            als die Standard-Immobilienfotografie. Das Ziel ist kein perfekt
            komponiertes Flachbild – es ist ein nahtloses sphärisches Panorama,
            das den Raum von einem einzelnen Standpunkt aus genau darstellt.
            Beherrschen Sie diese Techniken, und Ihre Virtual Tours werden
            durchgängig professionell aussehen.
          </p>
        </section>

        <section>
          <h2>Beleuchtungsaufbau für 360°-Innenraumfotografie</h2>
          <h3>Natürliches Licht – Ihr bester Freund</h3>
          <p>
            Fotografieren Sie zur hellsten Tageszeit relativ zur
            Fensterausrichtung jedes Raumes. Öffnen Sie alle Vorhänge und
            Jalousien vollständig. Natürliches Licht erzeugt die realistischste
            Darstellung davon, wie sich ein Raum tatsächlich anfühlt. Vermeiden
            Sie es, direkt in unabgeschirmte Fenster zu fotografieren – wenn der
            Außenbereich überbelichtet ist, verwenden Sie ein Diffusionsfilm
            oder fotografieren Sie, wenn die Sonne nicht direkt auf das Fenster
            ausgerichtet ist.
          </p>
          <h3>Künstliche Beleuchtung</h3>
          <p>
            Schalten Sie alle Deckenleuchten, Stehlampen und
            Unterschrank-Beleuchtung ein. Gemischte Lichttemperaturen (warm vs.
            kalt) können Farbstich-Probleme verursachen – standardisieren Sie
            nach Möglichkeit die Glühbirnentemperaturen im gesamten Objekt (alle
            warm oder alle tageslicht). LED-Panels oder Bounce-Lichter in Ecken
            helfen, Schattenbereiche zu füllen, ohne im Panorama sichtbar zu
            sein.
          </p>
        </section>

        <section>
          <h2>Kamerapositionierung für 360°-Innenraumfotografie</h2>
          <h3>Zentrale Position</h3>
          <p>
            Stellen oder positionieren Sie Ihre Kamera im geometrischen
            Mittelpunkt des Raumes. Dies maximiert die sichtbare Bodenfläche und
            sorgt für gleiche Abdeckung in alle Richtungen. Eine außermittige
            Positionierung lässt eine Wand im Panorama dominieren, was das
            Raumgefühl mindert.
          </p>
          <h3>Höhe</h3>
          <p>
            Nehmen Sie auf 1,2–1,5 m Höhe für Wohninnenräume auf. Bei
            Gewerberäumen mit hohen Decken funktionieren 1,5–1,7 m besser, um
            die vertikale Perspektive auszugleichen. Vermeiden Sie Aufnahmen auf
            Bodenhöhe (lässt alles übergroß wirken) oder über 1,8 m (lässt Räume
            institutionell wirken).
          </p>
          <h3>Freie Sichtlinien</h3>
          <p>
            Stellen Sie sicher, dass keine großen Möbelstücke, Türen oder
            Objekte wichtige Sichtlinien blockieren. Das Ziel ist, so viel wie
            möglich vom Raum vom Aufnahmestandpunkt aus zu zeigen. Wenn ein
            wichtiges Merkmal (Kamin, Kücheninsel) von der zentralen Position
            aus verdeckt ist, nehmen Sie ein ergänzendes Panorama von einer
            Position auf, die es klar zeigt.
          </p>
        </section>

        <section>
          <h2>Nachbearbeitungs-Tipps für 360°-Innenraumfotos</h2>
          <p>
            Nach der Aufnahme und dem KI-Stitching: Überprüfen Sie Nahtlinien an
            kontrastreichen Bereichen (Fenster, Leuchten) und glätten Sie sie
            bei Bedarf manuell. Passen Sie die globale Belichtung etwas heller
            an, als Sie denken, notwendig zu sein – 360°-Betrachter nehmen
            Touren tendenziell etwas dunkler wahr als Flachfotos. Stellen Sie
            sicher, dass die Horizontlinie perfekt gerade ist – jede Neigung ist
            in einem 360°-Viewer sofort spürbar.
          </p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/360-immobilienfotografie-leitfaden">360°-Immobilienfotografie : Leitfaden für Makler</a></li>
            <li><a href="/blog/de/360-fotos-mit-smartphone-aufnehmen">360°-Fotos mit Smartphone : Vollständige Anleitung</a></li>
            <li><a href="/blog/de/unscharfe-360-fotos-beheben">Unscharfe 360°-Fotos beheben : Ursachen & Lösungen</a></li>
            <li><a href="/blog/de/360-fotografie-fehler-vermeiden">360°-Fotografie: 9 Fehler vermeiden</a></li>
            <li><a href="/blog/de/immobilien-virtual-tour-smartphone">Immobilien Virtual Tour Smartphone</a></li>
            <li><a href="/blog/de/360-virtual-tour">360° Virtual Tour : Der komplette Leitfaden</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "innenraum-fotografie-360-virtual-tour",
);

useBlogSeo({
  title: "Innenraum-Fotografie & 360° Virtual Tour [2026] | Travvir",
  description:
    "Wie Innenraumfotografie und 360°-Virtual-Tours zusammenwirken. Beleuchtung, Winkel und Technik für überzeugende Immobilienpräsentationen.",
  slug: "innenraum-fotografie-360-virtual-tour",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
